import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default auth header if token exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Generic API methods for direct use (api.get, api.post, etc.)
const api = {
  get: (url, config) => axios.get(`${API}${url}`, config),
  post: (url, data, config) => axios.post(`${API}${url}`, data, config),
  put: (url, data, config) => axios.put(`${API}${url}`, data, config),
  delete: (url, config) => axios.delete(`${API}${url}`, config),
  patch: (url, data, config) => axios.patch(`${API}${url}`, data, config),
};

// Auth
export const authAPI = {
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  me: () => axios.get(`${API}/auth/me`),
};

// Users
export const usersAPI = {
  getAll: () => axios.get(`${API}/users`),
  update: (id, data) => axios.put(`${API}/users/${id}`, data),
};

// Contractors
export const contractorsAPI = {
  getAll: () => axios.get(`${API}/contractors`),
  getOne: (id) => axios.get(`${API}/contractors/${id}`),
  create: (data) => axios.post(`${API}/contractors`, data),
  update: (id, data) => axios.put(`${API}/contractors/${id}`, data),
  delete: (id) => axios.delete(`${API}/contractors/${id}`),
};

// Supplier reminders (auto emails N days before on-site)
export const remindersAPI = {
  getSettings: () => axios.get(`${API}/settings/reminders`),
  updateSettings: (data) => axios.put(`${API}/settings/reminders`, data),
  due: (days) => axios.get(`${API}/reminders/due`, { params: days != null ? { days } : {} }),
  draft: (jobId) => axios.get(`${API}/reminders/draft`, { params: { job_id: jobId } }),
  send: (data) => axios.post(`${API}/reminders/send`, data),
  sent: () => axios.get(`${API}/reminders/sent`),
};

// AI provider (company OpenAI key / BYOK)
export const aiKeyAPI = {
  getStatus: () => axios.get(`${API}/settings/ai-key`),
  save: (apiKey) => axios.put(`${API}/settings/ai-key`, { api_key: apiKey }),
  remove: () => axios.delete(`${API}/settings/ai-key`),
};

// Email Inbox (IMAP) + AI PO scanning
export const emailInboxAPI = {
  getAccount: () => axios.get(`${API}/email-account`),
  connect: (data) => axios.post(`${API}/email-account`, data),
  disconnect: () => axios.delete(`${API}/email-account`),
  scan: () => axios.post(`${API}/email/scan`),
  scanStatus: () => axios.get(`${API}/email/scan-status`),
  parseEmail: (data) => axios.post(`${API}/ai/parse-po-email`, data),
  getInbox: (params) => axios.get(`${API}/po-inbox`, { params }),
  importCandidate: (id) => axios.post(`${API}/po-inbox/${id}/import`),
  dismissCandidate: (id) => axios.post(`${API}/po-inbox/${id}/dismiss`),
  createTest: (data) => axios.post(`${API}/po-inbox/create-test`, data),
  draftReply: (id, data) => axios.post(`${API}/po-inbox/${id}/draft-reply`, data),
  sendReply: (id, data) => axios.post(`${API}/po-inbox/${id}/send-reply`, data),
};

// AI Executive Assistant (automation)
export const eaAPI = {
  getSettings: () => axios.get(`${API}/settings/ea`),
  updateSettings: (data) => axios.put(`${API}/settings/ea`, data),
  runNow: () => axios.post(`${API}/ea/run-now`),
  digestPreview: () => axios.get(`${API}/ea/digest-preview`),
  actionList: () => axios.get(`${API}/action-list`),
  resolveInvoice: (id) => axios.post(`${API}/trade-invoices/${id}/resolve`),
  snooze: (itemId) => axios.post(`${API}/action-list/snooze`, { item_id: itemId }),
  sendTestDigest: () => axios.post(`${API}/ea/send-test-digest`),
  sendWeeklyTest: () => axios.post(`${API}/ea/send-weekly-test`),
  threadUpdates: (status = 'pending') => axios.get(`${API}/ea/thread-updates`, { params: { status } }),
  applyThreadUpdate: (id) => axios.post(`${API}/ea/thread-updates/${id}/apply`),
  dismissThreadUpdate: (id) => axios.post(`${API}/ea/thread-updates/${id}/dismiss`),
  paymentMatches: (status = 'pending') => axios.get(`${API}/payment-matches`, { params: { status } }),
  applyPaymentMatch: (id) => axios.post(`${API}/payment-matches/${id}/apply`),
  dismissPaymentMatch: (id) => axios.post(`${API}/payment-matches/${id}/dismiss`),
  getReportSettings: () => axios.get(`${API}/settings/reports`),
  updateReportSettings: (data) => axios.put(`${API}/settings/reports`, data),
};

// Suppliers (builders / insurers that send us POs)
export const suppliersAPI = {
  getAll: () => axios.get(`${API}/suppliers`),
  getOne: (id) => axios.get(`${API}/suppliers/${id}`),
  create: (data) => axios.post(`${API}/suppliers`, data),
  update: (id, data) => axios.put(`${API}/suppliers/${id}`, data),
  delete: (id) => axios.delete(`${API}/suppliers/${id}`),
  getTypes: () => axios.get(`${API}/supplier-types`),
};

// Jobs
export const jobsAPI = {
  getAll: (params) => axios.get(`${API}/jobs`, { params }),
  getOne: (id) => axios.get(`${API}/jobs/${id}`),
  getFull: (id) => axios.get(`${API}/jobs/${id}/full`),
  getDocuments: (id, params) => axios.get(`${API}/jobs/${id}/documents`, { params }),
  getActivity: (id) => axios.get(`${API}/jobs/${id}/activity`),
  getEmails: (id) => axios.get(`${API}/jobs/${id}/emails`),
  create: (data) => axios.post(`${API}/jobs`, data),
  update: (id, data) => axios.put(`${API}/jobs/${id}`, data),
  updateStatus: (id, status) => axios.put(`${API}/jobs/${id}/status`, { status }),
  delete: (id) => axios.delete(`${API}/jobs/${id}`),
  uploadImage: (id, formData) => axios.post(`${API}/jobs/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (jobId, imageIndex) => axios.delete(`${API}/jobs/${jobId}/images/${imageIndex}`),
  sendEmail: (id, data) => axios.post(`${API}/jobs/${id}/send-email`, data),
  getInvoicedJobs: (params) => axios.get(`${API}/invoiced-jobs`, { params }),
  createQuoteFromPO: (id) => axios.post(`${API}/jobs/${id}/create-quote`),
  logInvoice: (id, data) => axios.post(`${API}/jobs/${id}/log-invoice`, data),
  uploadInvoice: (id, formData) => axios.post(`${API}/jobs/${id}/upload-invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Quotes
export const quotesAPI = {
  getAll: (params) => axios.get(`${API}/quotes`, { params }),
  getOne: (id) => axios.get(`${API}/quotes/${id}`),
  create: (data) => axios.post(`${API}/quotes`, data),
  update: (id, data) => axios.put(`${API}/quotes/${id}`, data),
  downloadPdf: (id) => axios.get(`${API}/quotes/${id}/pdf`, { responseType: 'blob' }),
  generatePO: (id, data) => axios.post(`${API}/quotes/${id}/generate-po`, data),
  duplicate: (id, data) => axios.post(`${API}/quotes/${id}/duplicate`, data || {}),
  createRevision: (id) => axios.post(`${API}/quotes/${id}/create-revision`),
  rename: (id, data) => axios.put(`${API}/quotes/${id}/rename`, data),
  generateVariation: (id, data) => axios.post(`${API}/quotes/${id}/generate-variation`, data),
  getRevisions: (id) => axios.get(`${API}/quotes/${id}/revisions`),
};

// Trade Types & Common Items
export const tradeAPI = {
  getTradeTypes: () => axios.get(`${API}/trade-types`),
  createTradeType: (data) => axios.post(`${API}/trade-types`, data),
  deleteTradeType: (name) => axios.delete(`${API}/trade-types/${encodeURIComponent(name)}`),
  getRegions: () => axios.get(`${API}/regions`),
  getCommonPOItems: () => axios.get(`${API}/common-po-items`),
  getEmailTemplates: () => axios.get(`${API}/email-templates`),
  getQuoteSections: () => axios.get(`${API}/quote-sections`),
};

// Company Branding
export const brandingAPI = {
  get: () => axios.get(`${API}/branding`),
  update: (data) => axios.put(`${API}/branding`, data),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  getAll: (params) => axios.get(`${API}/purchase-orders`, { params }),
  getOne: (id) => axios.get(`${API}/purchase-orders/${id}`),
  create: (data) => axios.post(`${API}/purchase-orders`, data),
  update: (id, data) => axios.put(`${API}/purchase-orders/${id}`, data),
  cancel: (id, data) => axios.post(`${API}/purchase-orders/${id}/cancel`, data),
  downloadPdf: (id) => axios.get(`${API}/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  sendTrade: (id, data) => axios.post(`${API}/purchase-orders/${id}/send-trade`, data),
  getTradeLink: (id) => axios.get(`${API}/purchase-orders/${id}/trade-link`),
  regenerateLink: (id) => axios.post(`${API}/purchase-orders/${id}/regenerate-link`),
};

// Trade invoice upload (public — no auth)
export const tradeInvoiceAPI = {
  info: (token) => axios.get(`${API}/trade-invoice/${token}`),
  upload: (token, formData) => axios.post(`${API}/trade-invoice/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Pricing
export const pricingAPI = {
  getAll: (params) => axios.get(`${API}/pricing`, { params }),
  create: (data) => axios.post(`${API}/pricing`, data),
  update: (id, data) => axios.put(`${API}/pricing/${id}`, data),
  delete: (id) => axios.delete(`${API}/pricing/${id}`),
};

// Quote Templates
export const quoteTemplatesAPI = {
  getAll: () => axios.get(`${API}/quote-templates`),
  getOne: (id) => axios.get(`${API}/quote-templates/${id}`),
  create: (data) => axios.post(`${API}/quote-templates`, data),
  update: (id, data) => axios.put(`${API}/quote-templates/${id}`, data),
  delete: (id) => axios.delete(`${API}/quote-templates/${id}`),
  upload: (formData) => axios.post(`${API}/quote-templates/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => axios.get(`${API}/invoices`, { params }),
  getOne: (id) => axios.get(`${API}/invoices/${id}`),
  create: (data) => axios.post(`${API}/invoices`, data),
  update: (id, data) => axios.put(`${API}/invoices/${id}`, data),
  downloadPdf: (id) => axios.get(`${API}/invoices/${id}/pdf`, { responseType: 'blob' }),
  exportExcel: (id) => axios.get(`${API}/invoices/${id}/export/excel`, { responseType: 'blob' }),
  importInvoice: (formData) => axios.post(`${API}/invoices/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Schedules
export const schedulesAPI = {
  getAll: (params) => axios.get(`${API}/schedules`, { params }),
  create: (data) => axios.post(`${API}/schedules`, data),
  update: (id, data) => axios.put(`${API}/schedules/${id}`, data),
  delete: (id) => axios.delete(`${API}/schedules/${id}`),
};

// Email
export const emailAPI = {
  send: (data) => axios.post(`${API}/email/send`, data),
  getLogs: (params) => axios.get(`${API}/email/logs`, { params }),
};

// Activity Logs
export const activityAPI = {
  getLogs: (params) => axios.get(`${API}/activity-logs`, { params }),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`),
};

// Reports (next-day AI daily report)
export const reportsAPI = {
  getJobsSummary: (params) => axios.get(`${API}/reports/jobs-summary`, { params }),
  getFinancial: (params) => axios.get(`${API}/reports/financial`, { params }),
  nextDay: (date) => axios.get(`${API}/reports/next-day`, { params: date ? { date } : {} }),
  generateNextDay: (date) => axios.post(`${API}/reports/next-day/generate`, null, { params: date ? { date } : {} }),
  listDaily: () => axios.get(`${API}/reports/daily`),
};

// AI Services
export const aiAPI = {
  generateQuote: (data) => axios.post(`${API}/ai/generate-quote`, data),
  suggestTrades: (jobId) => axios.post(`${API}/ai/suggest-trades?job_id=${jobId}`),
  createJob: (data) => axios.post(`${API}/ai/create-job`, data),
  draftEmail: (data) => axios.post(`${API}/ai/draft-email`, data),
  getEmailTemplates: () => axios.get(`${API}/ai/email-templates`),
  estimateCost: (data) => axios.post(`${API}/ai/estimate-cost`, data),
  validateQuote: (quoteId) => axios.post(`${API}/ai/validate-quote?quote_id=${quoteId}`),
  optimizeSchedule: (data) => axios.post(`${API}/ai/optimize-schedule`, data),
  analyzeJob: (jobId) => axios.post(`${API}/ai/analyze-job?job_id=${jobId}`),
};

// Client Portal (public)
export const clientAPI = {
  getQuote: (token) => axios.get(`${API}/client/quote/${token}`),
  approveQuote: (token) => axios.post(`${API}/client/quote/${token}/approve`),
  rejectQuote: (token, reason) => axios.post(`${API}/client/quote/${token}/reject`, null, { params: { reason } }),
  downloadPdf: (token) => axios.get(`${API}/client/quote/${token}/pdf`, { responseType: 'blob' }),
};

// Seed Data
export const seedAPI = {
  seedData: () => axios.post(`${API}/seed-data`),
};

// Documents
export const documentsAPI = {
  getAll: (params) => axios.get(`${API}/documents`, { params }),
  getOne: (id) => axios.get(`${API}/documents/${id}`),
  upload: (formData) => axios.post(`${API}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => axios.put(`${API}/documents/${id}`, data),
  delete: (id) => axios.delete(`${API}/documents/${id}`),
  getLinkedOptions: () => axios.get(`${API}/documents/linked-entities/options`),
};

// Companies (Multi-company support)
export const companiesAPI = {
  getAll: () => axios.get(`${API}/companies`),
  getOne: (id) => axios.get(`${API}/companies/${id}`),
  create: (data) => axios.post(`${API}/companies`, data),
  update: (id, data) => axios.put(`${API}/companies/${id}`, data),
  invite: (id, data) => axios.post(`${API}/companies/${id}/invite`, data),
  removeUser: (companyId, userId) => axios.delete(`${API}/companies/${companyId}/users/${userId}`),
  switch: (id) => axios.post(`${API}/companies/${id}/switch`),
};

// Recurring Templates
export const recurringTemplatesAPI = {
  getAll: () => axios.get(`${API}/recurring-templates`),
  getOne: (id) => axios.get(`${API}/recurring-templates/${id}`),
  create: (data) => axios.post(`${API}/recurring-templates`, data),
  update: (id, data) => axios.put(`${API}/recurring-templates/${id}`, data),
  delete: (id) => axios.delete(`${API}/recurring-templates/${id}`),
  toggle: (id) => axios.post(`${API}/recurring-templates/${id}/toggle`),
  runNow: (id) => axios.post(`${API}/recurring-templates/${id}/run-now`),
};

export default {
  // Generic methods for direct API calls
  get: api.get,
  post: api.post,
  put: api.put,
  delete: api.delete,
  patch: api.patch,
  // Named API modules
  auth: authAPI,
  users: usersAPI,
  contractors: contractorsAPI,
  jobs: jobsAPI,
  quotes: quotesAPI,
  purchaseOrders: purchaseOrdersAPI,
  pricing: pricingAPI,
  invoices: invoicesAPI,
  schedules: schedulesAPI,
  email: emailAPI,
  activity: activityAPI,
  dashboard: dashboardAPI,
  reports: reportsAPI,
  ai: aiAPI,
  client: clientAPI,
  seed: seedAPI,
  documents: documentsAPI,
};
